import {useCallback, useMemo} from 'react';

import {ScreeningApplicantFilters} from '../features/company/screening/ApplicantScreeningFilters/ApplicantScreeningFilters';
import {
  FilterOptions,
  ScreeningApplicant,
  ScreeningTotalCountPerStatus,
} from '../features/company/screening/types/screeningExternalApplicantsTypes';
import {GetRoutes} from '../lib/api/GET/GetRoutes';
import {AppConfig} from '../lib/AppConfig';
import {Option} from '../types/shared-types';

import {useApiRead} from './useApiRead';
import {useQueryPagination} from './useQueryPagination';
import {LocalQueryParams, useQueryParameterState} from './useQueryParameterState';

interface ScreeningModuleParams extends LocalQueryParams {
  view?: string;
  lang?: string;
  tab?: string;
  location?: string;
  job_posting?: string;
  departments?: string;
  sc_page?: string;
}

const STATUS_TO_TAB: Record<string, string> = {
  inviteCreated: 'invite-created',
  assessmentStarted: 'assessment-started',
  completed: 'done',
};
const TAB_TO_STATUS: Record<string, string> = {
  'invite-created': 'inviteCreated',
  'assessment-started': 'assessmentStarted',
  done: 'completed',
};

interface Args {
  isEnabled?: boolean;
  companyId: string;
}

export const useScreeningApplicantSorting = ({isEnabled, companyId}: Args) => {
  const {updateQueryParameters, params} = useQueryParameterState<ScreeningModuleParams>();

  const tablePage = useMemo(() => {
    const parsed = parseInt(params.sc_page ?? '', 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  }, [params.sc_page]);

  const filterParams = useMemo((): ScreeningModuleParams | undefined => {
    const next: ScreeningModuleParams = {};

    if (params.job_posting) {
      next.job_posting = params.job_posting;
    }
    if (params.location) {
      next.location = params.location;
    }
    if (params.tab) {
      next.tab = params.tab;
    }
    if (params.departments) {
      next.departments = params.departments;
    }

    return Object.keys(next).length ? next : undefined;
  }, [params.job_posting, params.location, params.tab, params.departments]);

  const {
    data,
    pagination,
    hasNext,
    getNext,
    totalCountPerStatus,
    loadingInitialData,
    isLoading: screeningIsLoading,
    refreshData,
  } = useQueryPagination<ScreeningApplicant, ScreeningTotalCountPerStatus, ScreeningModuleParams>({
    entity: 'screening-applicants',
    url: GetRoutes.screeningApplicants(companyId),
    reverseOrder: true,
    perPage: AppConfig.PAGINATION_PER_PAGE,
    filterParams,
    enabled: isEnabled,
  });

  const {
    data: filtersOptions,
    refetch: refetchFilters,
    isLoading: isFilterLoading,
  } = useApiRead<FilterOptions>({
    path: GetRoutes.screeningApplicantsFilterOptions(companyId),
    options: {
      enabled: isEnabled,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });

  const initialStatuses = useMemo<Option[]>(() => {
    const tabs = params.tab?.split(',').filter(Boolean) || [];
    return tabs.map((tab) => ({
      id: TAB_TO_STATUS[tab] || tab,
      label: TAB_TO_STATUS[tab] || tab,
    }));
  }, [params.tab]);

  const initialLocations = useMemo<Option[]>(
    () => (params.location?.split(',').filter(Boolean) || []).map((id) => ({id, label: id})),
    [params.location]
  );
  const initialPositions = useMemo<Option[]>(
    () => (params.job_posting?.split(',').filter(Boolean) || []).map((id) => ({id, label: id})),
    [params.job_posting]
  );
  const initialDepartments = useMemo<Option[]>(
    () => (params.departments?.split(',').filter(Boolean) || []).map((id) => ({id, label: id})),
    [params.departments]
  );

  const updateScreeningQueryParams = useCallback(
    (next: Partial<ScreeningModuleParams>) => {
      updateQueryParameters({
        ...params,
        ...next,
      });
    },
    [updateQueryParameters, params]
  );

  const filterBy = useCallback((filters: ScreeningApplicantFilters) => {
    const {position, location, status, department} = filters;
    const tabs = status.map((o) => STATUS_TO_TAB[o.id as string]).filter(Boolean);

    updateScreeningQueryParams({
      job_posting: position.length ? position.map((o) => String(o.id)).join(',') : '',
      location: location.length ? location.map((o) => String(o.id)).join(',') : '',
      tab: tabs.length ? tabs.join(',') : '',
      departments: department.length ? department.map((o) => String(o.id)).join(',') : '',
      sc_page: '',
    });
  }, [updateScreeningQueryParams]);

  const setTablePage = useCallback((nextPage: number) => {
    const safePage = Math.max(1, nextPage);
    updateScreeningQueryParams({
      sc_page: safePage <= 1 ? '' : String(safePage),
    });
  }, [updateScreeningQueryParams]);

  return {
    screeningApplicants: data,
    totalCountPerStatus,
    loadingInitialData,
    screeningIsLoading,
    refreshData,
    pagination,
    hasNext,
    getNext,
    refetchFilters,
    filtersOptions,
    isLoading: isFilterLoading || loadingInitialData,
    filterBy,
    statusOptions: ['completed', 'assessmentStarted', 'inviteCreated'],
    initialStatuses,
    initialLocations,
    initialPositions,
    initialDepartments,
    tablePage,
    setTablePage,
  };
};
